import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';
import { createSliceEntityAdapter, EntityState } from 'ngrx-slice/entity';

import { PackageDetailedResponse, PackagePageResponse } from '@app/api/models';
import { PackagePageRequest } from '@packaging/interfaces/package-page-request.interface';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { ContentStatus } from '@shared/interfaces/content-status';
import { PageResponse } from '@shared/interfaces/page-response.interface';

export interface State {
  // get list
  packagesFilter: PackagePageRequest;
  packages: EntityState<PackagePageResponse>;
  isLoading: boolean;
  packagesLoadError: boolean;
  totalPackages: number;
  // get one package to edit
  editedPackage: PackageDetailedResponse | undefined;
  isEditedPackageLoading: boolean;
  editedPackageLoadError: boolean;
  // save package
  packageSaving: boolean;
  packageSaveError: boolean;
}

export const packageAdapter = createSliceEntityAdapter<PackagePageResponse>({
  selectId: (packageItem) => packageItem.id ?? '',
});

export const initialState: State = {
  packagesFilter: {
    status: ContentStatus.Draft,
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
  },
  packages: packageAdapter.getInitialState(),
  totalPackages: 0,
  isLoading: false,
  packagesLoadError: false,
  editedPackage: undefined,
  isEditedPackageLoading: false,
  editedPackageLoadError: false,
  packageSaving: false,
  packageSaveError: false,
};

export const {
  actions: PackagingActions,
  selectors,
  ...PackagingFeature
} = createSlice({
  name: 'packaging',
  initialState,
  reducers: {
    loadPackages: {
      trigger: (state, filter: PayloadAction<PackagePageRequest>) => {
        packageAdapter.setAll(state.packages, []);
        state.packagesFilter = filter;
        state.isLoading = true;
      },
      success: (
        state,
        action: PayloadAction<PageResponse<PackagePageResponse>>
      ) => {
        packageAdapter.setAll(state.packages, action.content);
        state.totalPackages = action.totalElements ?? 0;
        state.isLoading = false;
        state.packagesLoadError = false;
      },
      failure: (state) => ({
        ...state,
        packagesLoadError: true,
        isLoading: false,
      }),
    },
    loadEditedPackage: {
      trigger: (state, action: PayloadAction<{ id: string }>): State => ({
        ...state,
        ...action,
        isEditedPackageLoading: true,
      }),
      success: (state, action: PayloadAction<PackageDetailedResponse>) => {
        state.editedPackage = action;
        state.isEditedPackageLoading = false;
        state.editedPackageLoadError = false;
      },
      failure: (state) => ({
        ...state,
        editedPackageLoadError: true,
        isEditedPackageLoading: false,
      }),
    },
    createPackage: {
      trigger: (
        state,
        _action: PayloadAction<PackageDetailedResponse>
      ): State => ({
        ...state,
        packageSaving: true,
      }),
      success: (
        state,
        _action: PayloadAction<PackageDetailedResponse>
      ): State => ({
        ...state,
        packageSaveError: false,
        packageSaving: false,
      }),
      failure: (state): State => ({
        ...state,
        packageSaveError: true,
        packageSaving: false,
      }),
    },
    updatePackage: {
      trigger: (
        state,
        _action: PayloadAction<{
          package: PackageDetailedResponse;
          isAdminMode?: boolean;
        }>
      ): State => ({
        ...state,
        packageSaving: true,
      }),
      success: (
        state,
        _action: PayloadAction<PackageDetailedResponse>
      ): State => ({
        ...state,
        packageSaveError: false,
        packageSaving: false,
      }),
      failure: (state): State => ({
        ...state,
        packageSaveError: true,
        packageSaving: false,
      }),
    },
    reset: (_) => initialState,
  },
});

export const PackagingSelectors = {
  ...selectors,
  selectPackageList: createSelector(selectors.selectPackages, (packages) =>
    Object.values(packages.entities)
  ),
  selectPackage: (id: string) =>
    createSelector(selectors.selectPackages, (packages) =>
      id ? packages.entities[id] : undefined
    ),
};
